/**
 * `useFeatureFlags`
 *
 * Subscribes to the single `config/featureFlags` Firestore document
 * and returns the merged-with-defaults flag set. Always returns a
 * fully-populated object — consumers can read a flag synchronously
 * on first render without null-guards.
 *
 * `loading` is true only until the first snapshot arrives; once
 * settled the hook stays in sync until unmount.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirestore } from '@/firebase';
import {
  DEFAULT_FEATURE_FLAGS,
  FEATURE_FLAGS_COLLECTION,
  FEATURE_FLAGS_DOC,
  type FeatureFlags,
  type FeatureFlagsDoc,
  type LegacyFeatureFlagsDoc,
} from '@/models/feature-flags';

export interface UseFeatureFlagsResult {
  flags: FeatureFlags;
  loading: boolean;
  /** Last snapshot error, or null. Hook does not throw. */
  error: Error | null;
}

/**
 * Hook variant that returns the live flag set plus loading + error.
 *
 * @returns the current flags (defaults-merged), a loading flag for
 *   the initial read, and any snapshot listener error.
 */
export function useFeatureFlags(): UseFeatureFlagsResult {
  const firestore = useFirestore();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const ref = doc(firestore, FEATURE_FLAGS_COLLECTION, FEATURE_FLAGS_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // No doc yet — return defaults rather than waiting for an
          // admin to write one. This is the expected initial state
          // on a fresh deploy.
          setFlags(DEFAULT_FEATURE_FLAGS);
        } else {
          const data = snap.data() as Partial<FeatureFlagsDoc> &
            Partial<LegacyFeatureFlagsDoc>;
          setFlags({
            officerMessagesEnabled:
              // Prefer the new field; fall back to the legacy
              // adminMessagesEnabled name so docs written before the
              // rename keep working.
              data.officerMessagesEnabled ??
              data.adminMessagesEnabled ??
              DEFAULT_FEATURE_FLAGS.officerMessagesEnabled,
            emailContactEnabled:
              data.emailContactEnabled ??
              DEFAULT_FEATURE_FLAGS.emailContactEnabled,
            contactFormEmailRecipients:
              data.contactFormEmailRecipients ??
              DEFAULT_FEATURE_FLAGS.contactFormEmailRecipients,
            registrationEmailRecipients:
              data.registrationEmailRecipients ??
              DEFAULT_FEATURE_FLAGS.registrationEmailRecipients,
          });
        }
        setLoading(false);
      },
      (err) => {
        // Permission denied on unauthenticated reads is common —
        // fall back to defaults so the public surface still renders.
        setError(err);
        setFlags(DEFAULT_FEATURE_FLAGS);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [firestore]);

  return { flags, loading, error };
}
